/**
 * drawerChrome — presentation primitives + persisted-width helpers for the
 * kanban CardDetailDrawer.
 *
 * Extracted verbatim from CardDetailDrawer.tsx (operator 500-line ceiling);
 * behaviour is unchanged. Holds the small, dependency-light pieces the
 * drawer's chrome needs: the tab union, drawer-width constants + reader,
 * the status/priority option tables + colour tokens, the ghost-control
 * class string, and the labelled `PropRow`.
 */
import { type ReactNode } from 'react'
import type { CardStatus } from '../../types'

/** Tabs surfaced inside the drawer. Email is conditional (hidden on
 *  manual cards with no `primary_email`); Files is always available
 *  but renders an empty state when there's nothing to show. The
 *  default tab is `card` — same UX as before this refactor. */
export type CardDetailTab = 'card' | 'email' | 'files'

/** localStorage key for the operator's chosen drawer width. Per-device
 *  so a desktop session with a 720 px drawer doesn't override a
 *  laptop session's 480 px choice in the same browser profile. */
export const WIDTH_LS_KEY = 'quotech.kanban.cardDrawerW'
/** Default widths — match the historic constants from the pre-resize
 *  drawer so existing operators see no visual change on first load. */
export const DEFAULT_WIDTH_OVERLAY_PX = 448 // Tailwind `max-w-md`
export const DEFAULT_WIDTH_PINNED_PX = 420 // PINNED_RAIL_WIDTH_PX
export const MIN_DRAWER_WIDTH_PX = 360
export const MAX_DRAWER_WIDTH_PX = 960

export function readSavedDrawerWidth(): number | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(WIDTH_LS_KEY)
    if (!raw) return null
    const n = Number.parseInt(raw, 10)
    if (!Number.isFinite(n)) return null
    return Math.min(MAX_DRAWER_WIDTH_PX, Math.max(MIN_DRAWER_WIDTH_PX, n))
  } catch {
    return null
  }
}

export const STATUS_OPTIONS: Array<{ value: CardStatus; label: string }> = [
  { value: 'OPEN',     label: 'Open' },
  { value: 'SNOOZED',  label: 'Snoozed' },
  { value: 'BLOCKED',  label: 'Blocked' },
  { value: 'DONE',     label: 'Done' },
  { value: 'ARCHIVED', label: 'Archived' },
]

export const PRIORITY_OPTIONS: Array<{ value: number; label: string; cls: string }> = [
  { value: 0, label: 'Low',     cls: 'text-neutral-500' },
  { value: 1, label: 'Normal',  cls: 'text-blue-600' },
  { value: 2, label: 'High',    cls: 'text-amber-600' },
  { value: 3, label: 'Urgent',  cls: 'text-red-600' },
]

// Status colour tokens — drive the dot + value colour in the property
// list so state is legible at a glance instead of being plain text.
export const STATUS_META: Record<CardStatus, { dot: string; text: string }> = {
  OPEN:     { dot: 'bg-blue-500',    text: 'text-blue-700 dark:text-blue-300' },
  SNOOZED:  { dot: 'bg-amber-500',   text: 'text-amber-700 dark:text-amber-300' },
  BLOCKED:  { dot: 'bg-red-500',     text: 'text-red-700 dark:text-red-300' },
  DONE:     { dot: 'bg-emerald-500', text: 'text-emerald-700 dark:text-emerald-300' },
  ARCHIVED: { dot: 'bg-neutral-400', text: 'text-neutral-500 dark:text-neutral-400' },
}

// Ghost form control: invisible chrome until hover/focus so the property
// list reads like a spec sheet, not a wall of boxes (Linear/Height style).
export const GHOST_CONTROL =
  'min-w-0 rounded-md border border-transparent bg-transparent px-2 py-1 text-[13px] text-neutral-800 dark:text-neutral-100 transition-colors hover:bg-neutral-100 dark:hover:bg-neutral-800 focus:border-blue-400 focus:bg-white dark:focus:bg-neutral-950 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:opacity-60'

/** One labelled row in the card property list: a quiet [icon · label] on
 *  the left and an inline control on the right. Input → presentational. */
export function PropRow({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-3 py-2">
      <span className="flex w-24 shrink-0 items-center gap-2 text-[12.5px] font-medium text-neutral-500 dark:text-neutral-400">
        <span className="text-neutral-400 dark:text-neutral-500">{icon}</span>
        {label}
      </span>
      <div className="flex min-w-0 flex-1 items-center">{children}</div>
    </div>
  )
}
